import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { toast } from "sonner";
import { Search, Clock, Eye, AlertTriangle, UserX } from "lucide-react";
import { getInitials, formatDateShort } from "./customersUtils";
import { CustomerDetail } from "./CustomerDetail";
import { Pagination } from "./Pagination";

export function InactiveCustomersTab() {
  const { authAxios } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchInactive = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days, search: debouncedSearch, page: String(page), limit: "50" });
      const res = await authAxios.get(`/customers/inactive?${params.toString()}`);
      setCustomers(res.data.customers || []);
      setTotalPages(res.data.pages || 1);
      setTotalCount(res.data.total || 0);
    } catch { toast.error("Error cargando inativos"); }
    setLoading(false);
  }, [authAxios, days, debouncedSearch, page]);

  useEffect(() => { fetchInactive(); }, [fetchInactive]);

  return (
    <div className="space-y-5">
      <Card className="border-orange-200 bg-orange-50/50">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-orange-800">Clientes Inactivos</p>
            <p className="text-xs text-orange-600 mt-0.5">
              Clientes que no realizaron pedidos en los últimos <strong>{days}</strong> días.
              Total encontrado: <strong>{totalCount}</strong>
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row gap-3" data-testid="inactive-filters">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Buscar por nombre o teléfono..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="inactive-search" />
        </div>
        <Select value={days} onValueChange={v => { setDays(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-52" data-testid="inactive-days-filter">
            <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Inactivos 7+ días</SelectItem>
            <SelectItem value="14">Inactivos 14+ días</SelectItem>
            <SelectItem value="30">Inactivos 30+ días</SelectItem>
            <SelectItem value="60">Inactivos 60+ días</SelectItem>
            <SelectItem value="90">Inactivos 90+ días</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <LoadingScreen /> : (
        <>
          <div className="rounded-lg border border-border/50 overflow-hidden bg-card">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="pl-4">Cliente</TableHead>
                  <TableHead className="hidden md:table-cell">Celular</TableHead>
                  <TableHead>Pedidos</TableHead>
                  <TableHead>Total Gastado</TableHead>
                  <TableHead>Último Pedido</TableHead>
                  <TableHead>Días Inactivo</TableHead>
                  <TableHead className="w-[80px] text-center">Detalle</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map(c => (
                  <TableRow key={c.phone} className="cursor-pointer group" onClick={() => { setSelectedPhone(c.phone); setDetailOpen(true); }} data-testid={`inactive-row-${c.phone}`}>
                    <TableCell className="pl-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-xs font-bold text-orange-600 flex-shrink-0">{getInitials(c.name)}</div>
                        <span className="font-semibold text-sm">{c.name || "Sin nombre"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{c.phone}</TableCell>
                    <TableCell><span className="font-semibold">{c.total_orders}</span></TableCell>
                    <TableCell><span className="font-bold text-primary">{formatPrice(c.total_spent)}</span></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDateShort(c.last_order_at)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={`text-xs ${c.days_inactive > 60 ? "bg-red-100 text-red-700" : c.days_inactive > 30 ? "bg-orange-100 text-orange-700" : "bg-yellow-100 text-yellow-700"}`}>
                        {c.days_inactive}d
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100"><Eye className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {customers.length === 0 && (
              <div className="py-16 text-center">
                <UserX className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground">No hay clientes inactivos en este período</p>
              </div>
            )}
          </div>
          <Pagination page={page} pages={totalPages} total={totalCount} onPageChange={setPage} />
        </>
      )}

      <CustomerDetail phone={selectedPhone} open={detailOpen} onClose={() => { setDetailOpen(false); fetchInactive(); }} />
    </div>
  );
}
